import { useEffect } from 'react';
import { isAxiosError } from 'axios';
import { Footer } from './Footer';
import { useNavigate } from 'react-router-dom';
import { useHomePath } from 'src/auth/useHomePath';

import { captureFunnelEvent, trackError } from '../services/telemetry';
import { shouldReloadForChunkError } from '../utils/appErrors';

const SUPPORT_EMAIL = 'hello@pineapplemusiclab.com';
const CHUNK_RELOAD_KEY = 'pml.chunkReloadAt';

/** Reloads once when a deploy removed a chunk this tab needs. */
function reloadIfStaleChunk(error: Error): boolean {
  try {
    const stored = sessionStorage.getItem(CHUNK_RELOAD_KEY);
    const now = Date.now();
    if (
      !shouldReloadForChunkError(error, stored ? Number(stored) : null, now)
    ) {
      return false;
    }
    sessionStorage.setItem(CHUNK_RELOAD_KEY, String(now));
  } catch {
    // Storage blocked: without the loop guard, don't auto-reload.
    return false;
  }
  captureFunnelEvent('stale_chunk_reload', { message: error.message });
  window.location.reload();
  return true;
}

/**
 * Top-level error-boundary fallback (see `index.tsx`).
 *
 * This is the screen a paying customer photographed and sent us during the
 * revoked-token incident. It used to dump the axios error — including
 * `response.data` — as raw JSON, under a heading that read "Error" in red and
 * copy that unconditionally blamed our servers. That told the user nothing they
 * could act on, looked like a broken site rather than a recoverable hiccup, and
 * exposed backend response bodies to whoever was looking at the screen.
 *
 * The details are still captured — they go to telemetry, where they are useful —
 * but the page now offers a way forward instead of a stack of JSON.
 */
export function ErrorState({
  error,
  resetErrorBoundary,
}: {
  error: Error;
  resetErrorBoundary: () => void;
}) {
  const navigate = useNavigate();
  const homePath = useHomePath();
  const status = isAxiosError(error) ? error.response?.status : undefined;

  // Report once per crash. Anything that reaches this boundary is by definition
  // a page the user could not use, so it should never be silent to us.
  useEffect(() => {
    if (reloadIfStaleChunk(error)) return;
    console.error('Unhandled error reached the boundary:', error);
    trackError(error, {
      source: 'ErrorState',
      status,
      // Safe here (telemetry, not the screen) and the most useful field for
      // working out what the backend actually said.
      response_data: isAxiosError(error)
        ? JSON.stringify(error.response?.data)?.slice(0, 500)
        : undefined,
    });
  }, [error, status]);

  return (
    <>
      <div
        className={`
          my-4 flex flex-col items-center px-4 font-[Outfit]
          sm:my-6 sm:px-6
        `}
      >
        <div
          className={`
            flex w-full max-w-3xl flex-col items-center gap-y-6 text-gray-600
            sm:gap-y-8
          `}
        >
          <div
            className={`
              w-full rounded-lg bg-white p-4 text-center
              sm:p-6
              md:p-8
            `}
          >
            <h2
              className={`
                mb-3 text-xl font-bold text-gray-800
                sm:mb-4 sm:text-2xl
              `}
            >
              Something went wrong
            </h2>
            <p className="mb-4 text-sm text-gray-700 sm:text-base">
              We couldn&apos;t finish loading this page. Trying again usually
              fixes it. If it keeps happening, email us at{' '}
              <a
                href={`mailto:${SUPPORT_EMAIL}`}
                className="text-yellow-600 underline"
              >
                {SUPPORT_EMAIL}
              </a>{' '}
              and we&apos;ll take a look — your work and your subscription are
              not affected.
            </p>
            {status !== undefined && (
              // A short reference for support to match against logs. Deliberately
              // just the status: the response body belongs in telemetry, not on
              // a customer's screen.
              <p className="mb-4 text-xs text-gray-400">
                Reference: HTTP {status}
              </p>
            )}
            <div className="flex flex-wrap items-center justify-center gap-3">
              <button
                onClick={resetErrorBoundary}
                className={`
                  focus:ring-opacity-50 focus:ring-2 focus:ring-yellow-500
                  focus:outline-none
                  min-h-[44px] min-w-[44px] cursor-pointer rounded bg-yellow-500
                  px-4 py-2 text-sm text-white transition-transform duration-100
                  hover:bg-yellow-600
                  active:scale-95 active:bg-yellow-700
                  sm:text-base
                `}
              >
                Try again
              </button>
              <button
                onClick={() => {
                  resetErrorBoundary();
                  // Replace, so Back doesn't return to the page that failed.
                  navigate(homePath, { replace: true });
                }}
                className={`
                  focus:ring-opacity-50 focus:ring-2 focus:ring-yellow-500
                  focus:outline-none
                  min-h-[44px] min-w-[44px] cursor-pointer rounded border
                  border-gray-300 px-4 py-2 text-sm text-gray-700
                  transition-transform duration-100
                  hover:bg-gray-50
                  active:scale-95
                  sm:text-base
                `}
              >
                {homePath === '/workspace'
                  ? 'To My Projects'
                  : 'To the Home Page'}
              </button>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </>
  );
}
